import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Artifact } from '../../models/artifact.model';
import { ArtifactActionService } from '../../services/artifact-action.service';

/**
 * Native, interactive dashboard renderer. Registered under type 'dashboard' in
 * ARTIFACT_RENDERERS. Reads a JSON dashboard spec from `artifact.body` (the same
 * spec a scheduled run renders server-side to inline SVG) and draws each section
 * as DOM/SVG — accessible and themeable, no iframe.
 *
 * Spec shape (produced by the agent at run time):
 *   { title?, generated_at?, sections: [
 *       { id?, title, kind: 'kpi'|'bar'|'line'|'pie'|'table', encoding?, data } ] }
 *
 * Interactivity: clicking a bar/slice (drill-down) or the Refresh button emits a
 * follow-up message via ArtifactActionService; the chat re-invokes the agent,
 * which emits a fresh dashboard artifact that replaces this one in the panel.
 */

type ChartKind = 'kpi' | 'bar' | 'line' | 'pie' | 'table';

interface SpecSection {
  id?: string;
  title?: string;
  kind?: string;
  encoding?: Record<string, string>;
  data?: Record<string, unknown>;
}

interface BarDatum { label: string; value: number; display: string; y: number; w: number; }
interface PieSlice { d: string; color: string; label: string; value: number; pct: number; }

interface Card {
  id: string;
  title: string;
  kind: ChartKind;
  empty: boolean;
  // kpi
  kpiValue?: string;
  kpiLabel?: string;
  kpiSub?: string;
  // bar
  bars?: BarDatum[];
  barHeight?: number;   // svg viewBox height
  labelW?: number;
  trackW?: number;
  // line
  linePoints?: string;
  lineDots?: { x: number; y: number }[];
  lineLabels?: { x: number; label: string }[];
  lineAxisY?: number;
  // pie
  slices?: PieSlice[];
  // table
  columns?: string[];
  rows?: string[][];
}

const PALETTE = [
  '#d81e2c', '#2b6cb0', '#2f855a', '#b7791f',
  '#6b46c1', '#0987a0', '#c05621', '#718096',
];

@Component({
  selector: 'app-dashboard-artifact',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard-artifact.component.html',
  styleUrls: ['./dashboard-artifact.component.scss'],
})
export class DashboardArtifactComponent {
  private readonly actions = inject(ArtifactActionService);

  title = 'Dashboard';
  generatedAt = '';
  cards: Card[] = [];
  parseError = false;
  private _body?: string;

  @Input() set artifact(a: Artifact) {
    if (!a || a.body === this._body) return;
    this._body = a.body;
    this.title = a.title || 'Dashboard';
    this._build(a.body);
  }

  private _build(body: string | undefined): void {
    this.cards = [];
    this.parseError = false;
    this.generatedAt = '';
    if (!body) { this.parseError = true; return; }
    let spec: { title?: string; generated_at?: string; sections?: SpecSection[] };
    try {
      spec = JSON.parse(body);
    } catch {
      this.parseError = true;
      return;
    }
    if (spec.title) this.title = spec.title;
    this.generatedAt = spec.generated_at || '';
    const sections = Array.isArray(spec.sections) ? spec.sections : [];
    this.cards = sections.map((s, i) => this._card(s, i)).filter((c): c is Card => !!c);
  }

  private _card(sec: SpecSection, idx: number): Card | null {
    if (!sec || typeof sec !== 'object') return null;
    const id = String(sec.id || `section${idx}`);
    const title = String(sec.title || '');
    const kind = (['kpi', 'bar', 'line', 'pie', 'table'].includes(String(sec.kind))
      ? sec.kind : 'table') as ChartKind;
    const data = (sec.data && typeof sec.data === 'object' ? sec.data : {}) as Record<string, unknown>;
    const encoding = (sec.encoding && typeof sec.encoding === 'object' ? sec.encoding : {}) as Record<string, string>;
    try {
      switch (kind) {
        case 'kpi': return this._kpi(id, title, data);
        case 'bar': return this._bar(id, title, data, encoding);
        case 'line': return this._line(id, title, data, encoding);
        case 'pie': return this._pie(id, title, data, encoding);
        default: return this._table(id, title, data);
      }
    } catch {
      return { id, title, kind, empty: true };
    }
  }

  // ── data extraction (mirrors the server's _dash_pairs) ──────────────────────
  private _pairs(data: Record<string, unknown>, encoding: Record<string, string>): { label: string; value: number }[] {
    const xf = encoding['x'] || 'key';
    const yf = encoding['y'] || 'count';
    let rows: unknown = data['groups'];
    if (!Array.isArray(rows)) rows = Array.isArray(data['rows']) ? data['rows'] : [];
    const out: { label: string; value: number }[] = [];
    for (const r of rows as unknown[]) {
      if (!r || typeof r !== 'object') continue;
      const rec = r as Record<string, unknown>;
      const label = String(rec[xf] ?? rec['key'] ?? '');
      const raw = rec[yf] ?? rec['count'] ?? rec['value'] ?? 0;
      out.push({ label, value: this._num(raw) });
    }
    return out;
  }

  private _num(v: unknown): number {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : 0;
  }

  private _fmt(v: number): string {
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }

  private _kpi(id: string, title: string, data: Record<string, unknown>): Card {
    let value: unknown = data['value'];
    if (value == null) value = data['count'];
    if (value == null) value = data['revenue'];
    const kpiValue = value != null ? this._fmt(this._num(value)) : String(data['text'] ?? '—');
    const kpiLabel = String(data['label'] ?? data['unit'] ?? '');
    const kpiSub = data['count'] != null && data['revenue'] != null
      ? `${this._fmt(this._num(data['revenue']))} revenue` : '';
    return { id, title, kind: 'kpi', empty: false, kpiValue, kpiLabel, kpiSub };
  }

  private _bar(id: string, title: string, data: Record<string, unknown>, encoding: Record<string, string>): Card {
    const pairs = this._pairs(data, encoding).slice(0, 12);
    if (!pairs.length) return { id, title, kind: 'bar', empty: true };
    const maxv = Math.max(...pairs.map(p => p.value), 0) || 1;
    const barH = 22, gap = 12, trackW = 150;
    const bars: BarDatum[] = pairs.map((p, i) => ({
      label: p.label,
      value: p.value,
      display: this._fmt(p.value),
      y: gap + i * (barH + gap),
      w: Math.max(2, (p.value / maxv) * trackW),
    }));
    return {
      id, title, kind: 'bar', empty: false, bars,
      barHeight: gap + pairs.length * (barH + gap), labelW: 150, trackW,
    };
  }

  private _line(id: string, title: string, data: Record<string, unknown>, encoding: Record<string, string>): Card {
    const pairs = this._pairs(data, encoding);
    if (pairs.length < 2) return this._bar(id, title, data, encoding);
    const w = 320, h = 160, padL = 36, padR = 12, padT = 12, padB = 24;
    const plotW = w - padL - padR, plotH = h - padT - padB;
    const vals = pairs.map(p => p.value);
    const maxv = Math.max(...vals);
    const minv = Math.min(...vals, 0);
    const span = (maxv - minv) || 1;
    const n = pairs.length;
    const dots = pairs.map((p, i) => ({
      x: padL + plotW * i / (n - 1),
      y: padT + plotH * (1 - (p.value - minv) / span),
    }));
    const labelIdx = Array.from(new Set([0, Math.floor(n / 2), n - 1]));
    return {
      id, title, kind: 'line', empty: false,
      linePoints: dots.map(d => `${d.x.toFixed(1)},${d.y.toFixed(1)}`).join(' '),
      lineDots: dots,
      lineLabels: labelIdx.map(i => ({ x: padL + plotW * i / (n - 1), label: String(pairs[i].label).slice(0, 12) })),
      lineAxisY: padT + plotH,
    };
  }

  private _pie(id: string, title: string, data: Record<string, unknown>, encoding: Record<string, string>): Card {
    const pairs = this._pairs(data, encoding).filter(p => p.value > 0).slice(0, 8);
    if (!pairs.length) return { id, title, kind: 'pie', empty: true };
    const total = pairs.reduce((s, p) => s + p.value, 0) || 1;
    const cx = 80, cy = 80, r = 70;
    let start = -Math.PI / 2;
    const slices: PieSlice[] = pairs.map((p, i) => {
      const frac = p.value / total;
      const color = PALETTE[i % PALETTE.length];
      let d: string;
      if (pairs.length === 1) {
        d = `M${cx - r},${cy} a${r},${r} 0 1 0 ${r * 2},0 a${r},${r} 0 1 0 ${-r * 2},0`;
      } else {
        const end = start + frac * 2 * Math.PI;
        const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
        const large = frac > 0.5 ? 1 : 0;
        d = `M${cx.toFixed(1)},${cy.toFixed(1)} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 ${large} 1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`;
        start = end;
      }
      return { d, color, label: p.label, value: p.value, pct: Math.round(frac * 100) };
    });
    return { id, title, kind: 'pie', empty: false, slices };
  }

  private _table(id: string, title: string, data: Record<string, unknown>): Card {
    let columns = Array.isArray(data['columns']) ? (data['columns'] as unknown[]).map(String) : undefined;
    let rows: string[][] | undefined = Array.isArray(data['rows'])
      ? (data['rows'] as unknown[]).map(r => (Array.isArray(r) ? r.map(String) : [String(r)])) : undefined;
    const records = data['records'];
    if (!columns && Array.isArray(records) && records.length && typeof records[0] === 'object') {
      columns = Object.keys(records[0] as object).slice(0, 8);
      rows = (records as Record<string, unknown>[]).map(rec => columns!.map(c => String(rec[c] ?? '')));
    }
    const groups = data['groups'];
    if (!columns && Array.isArray(groups)) {
      columns = ['Key', 'Count'];
      rows = (groups as Record<string, unknown>[])
        .filter(g => g && typeof g === 'object')
        .map(g => [String(g['key'] ?? ''), String(g['count'] ?? '')]);
    }
    if (!columns || !rows || !rows.length) return { id, title, kind: 'table', empty: true };
    return { id, title, kind: 'table', empty: false, columns, rows: rows.slice(0, 50) };
  }

  // ── interactivity ───────────────────────────────────────────────────────────
  onSegment(card: Card, label: string): void {
    if (!label) return;
    this.actions.send(`In the "${card.title}" section, show me the details for ${label}.`);
  }

  onRefresh(): void {
    this.actions.send('Refresh this dashboard with the latest data.');
  }

  trackCard = (_: number, c: Card) => c.id;
}
